import React from 'react';
import { Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { ReportData } from '../../../types/report.types';

interface AppendixPageProps {
  reportData: ReportData;
  sectionId?: string;
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: '20mm 15mm',
    paddingBottom: '22mm',
    fontFamily: 'Roboto',
    fontSize: 10,
    color: '#1E293B',
    position: 'relative',
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    fontSize: 8.5,
    color: '#9CA3AF',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  footerLeft: {
    textAlign: 'left',
  },
  footerRight: {
    textAlign: 'right',
  },
  header: {
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
    paddingBottom: 8,
    marginBottom: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#0F172A',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  headerMeta: {
    fontSize: 8,
    color: '#64748B',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0F172A',
  },
  titleUnderlineRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    width: '100%',
    height: 3,
    marginTop: 4,
    marginBottom: 14,
  },
  titleUnderlineBlue: {
    width: 60,
    height: 3,
    backgroundColor: '#2563EB',
  },
  titleUnderlineDark: {
    flex: 1,
    height: 1.2,
    backgroundColor: '#1E293B',
  },
  paragraph: {
    lineHeight: 1.60,
    marginBottom: 12,
    color: '#475569',
    fontSize: 11.5,
  },
  table: {
    borderWidth: 1.2,
    borderColor: '#CBD5E1',
    marginTop: 8,
    marginBottom: 20,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1.8,
    borderBottomColor: '#475569',
  },
  tableHeaderCell: {
    color: '#4B5563',
    fontWeight: 'bold',
    fontSize: 9.0,
    paddingHorizontal: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
    minHeight: 28,
    alignItems: 'center',
  },
  tableRowCell: {
    fontSize: 10.0,
    paddingHorizontal: 12,
    color: '#334155',
    lineHeight: 1.45,
  },
});

const AppendixPageV2: React.FC<AppendixPageProps> = ({ reportData, sectionId }) => {
  const targets = reportData.target_details || [];
  
  const isNetworkTarget = (t: any) => {
    const type = (t.target_type || t.type || '').toLowerCase();
    const value = (t.target_value || t.url || t.target_name || '').toLowerCase();
    if (type.includes('web') || type.includes('api') || type.includes('app') || type.includes('mobile') || type.includes('ios') || type.includes('android')) {
      return false;
    }
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return false;
    }
    return type.includes('network') || type.includes('infra') || type.includes('subnet') || type.includes('range') || type.includes('ip') ||
           !!value.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/) ||
           /\/\s*\d{1,2}\s*$/.test(value);
  };

  const networkTargets = targets.filter(isNetworkTarget);
  const webTargets = targets.filter(t => !isNetworkTarget(t));

  const isNetwork = reportData.testing_type?.toLowerCase().includes('network') ||
    reportData.program_details?.[0]?.testing_type?.toLowerCase().includes('network') || (networkTargets.length > 0 && webTargets.length === 0);

  let parsedConfig: any = { globalFields: { description: true, instructions: true, impact: true, fix: true, evidence: true }, individualFindings: {} };
  if (reportData.finding_config) {
    if (typeof reportData.finding_config === 'string') {
      try {
        parsedConfig = JSON.parse(reportData.finding_config);
      } catch (_) {}
    } else {
      parsedConfig = reportData.finding_config;
    }
  }
  const config = parsedConfig;
  const networkSummary = config.networkScopeSummary || {
    totalAddresses: '',
    liveHosts: '',
    hostsWithFindings: '',
    position: ''
  };

  // Filter for API/Web targets
  const apiTargets = targets.filter(t => (t.target_type || t.type || '').toLowerCase().includes('api'));
  const hasEndpoints = apiTargets.some(t => t.endpoints && Array.isArray(t.endpoints) && t.endpoints.length > 0);

  const showNetworkAppendix = isNetwork || networkTargets.length > 0;
  
  // Group host IPs by their subnet (target_type) dynamically
  const groupedHosts: Record<string, string[]> = {};
  if (showNetworkAppendix) {
    const listToProcess = networkTargets.length > 0 ? networkTargets : targets;
    listToProcess.forEach(target => {
      const subnet = target.target_type || target.type || 'Other Ranges';
      const ip = target.target_value || target.url || '';
      if (ip) {
        if (!groupedHosts[subnet]) {
          groupedHosts[subnet] = [];
        }
        groupedHosts[subnet].push(ip);
      }
    });
  }
  
  const subnets = Object.keys(groupedHosts);
  const hasNetworkData = subnets.length > 0;
  const showApiAppendix = hasEndpoints;

  if (!hasNetworkData && !showApiAppendix) {
    return null; // Not needed if no network targets and no API endpoints list
  }

  const chunkArray = (arr: any[], size: number) => {
    const chunked = [];
    for (let i = 0; i < arr.length; i += size) {
      chunked.push(arr.slice(i, i + size));
    }
    return chunked;
  };

  const subnetTargets = targets.filter((t: any) => {
    const val = (t.target_value || t.url || '').toLowerCase();
    return val.includes('/') || val.includes('-');
  });
  const individualIpTargets = targets.filter((t: any) => {
    const val = (t.target_value || t.url || '').toLowerCase();
    return !val.includes('/') && !val.includes('-');
  });

  const subnetGroups: Record<string, string[]> = {};
  individualIpTargets.forEach(target => {
    const groupKey = target.target_type || target.type || 'Other Ranges';
    if (!subnetGroups[groupKey]) {
      subnetGroups[groupKey] = [];
    }
    const ip = target.target_value || target.url || '';
    if (ip) {
      subnetGroups[groupKey].push(ip);
    }
  });

  const allHostIps = targets
    .map(t => t.target_value || t.url || '')
    .filter(Boolean)
    .filter(val => !val.includes('/') && !val.includes('-'));

  const ipRows = chunkArray(allHostIps, 3);

  return (
    <Page size="A4" id={sectionId} style={styles.page}>
      {!isNetwork && (
        <View style={styles.header} fixed>
          <Text style={styles.headerTitle}>Appendix A - Host / Asset Inventory</Text>
          <Text style={styles.headerMeta}>{reportData.document_number || 'Pentest Report'}</Text>
        </View>
      )}

      <Text style={styles.sectionTitle}>
        {isNetwork ? 'Appendix A - In-Scope Hosts (IP Addresses)' : 'Technical Asset Catalog'}
      </Text>
      {isNetwork ? (
        <View style={styles.titleUnderlineRow}>
          <View style={styles.titleUnderlineBlue} />
          <View style={styles.titleUnderlineDark} />
        </View>
      ) : null}

      <Text style={styles.paragraph}>
        {isNetwork 
          ? `Complete list of in-scope live hosts identified during the engagement (${allHostIps.length} total). Subnets in scope: ${subnetTargets.map(s => s.target_value || s.url).join(', ')}.`
          : 'Complete list of in-scope live hosts and authorized endpoints identified during the engagement.'}
      </Text>

      {isNetwork && allHostIps.length > 0 && (
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, { width: '33.3%' }]}>EXTERNAL IP / HOST TARGET</Text>
            <Text style={[styles.tableHeaderCell, { width: '33.3%' }]}>EXTERNAL IP / HOST TARGET</Text>
            <Text style={[styles.tableHeaderCell, { width: '33.3%' }]}>EXTERNAL IP / HOST TARGET</Text>
          </View>
          {ipRows.map((row, rowIdx) => {
            const isLast = rowIdx === ipRows.length - 1;
            return (
              <View key={rowIdx} style={[styles.tableRow, { backgroundColor: rowIdx % 2 === 0 ? '#F8FAFC' : '#FFFFFF', borderBottomWidth: isLast ? 0 : 1, minHeight: 20, paddingVertical: 4 }]} wrap={false}>
                <Text style={[styles.tableRowCell, { width: '33.3%', fontFamily: 'Courier', fontSize: 8.5 }]}>{row[0] || ''}</Text>
                <Text style={[styles.tableRowCell, { width: '33.3%', fontFamily: 'Courier', fontSize: 8.5 }]}>{row[1] || ''}</Text>
                <Text style={[styles.tableRowCell, { width: '33.3%', fontFamily: 'Courier', fontSize: 8.5 }]}>{row[2] || ''}</Text>
              </View>
            );
          })}
        </View>
      )}

      {!isNetwork && hasNetworkData && (
        <>
          <Text style={[styles.sectionTitle, { fontSize: 12, marginTop: 10 }]}>Network IP Inventory</Text>
          {subnets.map((subnet, sIdx) => {
            const ips = groupedHosts[subnet] || [];
            const ipRows = chunkArray(ips, 3);
            return (
              <View key={sIdx} style={{ marginBottom: 15 }}>
                <Text style={{ fontSize: 9, fontWeight: 'bold', color: '#1E3A8A', marginBottom: 4, textTransform: 'uppercase' }}>
                  {subnet} ({ips.length} hosts)
                </Text>
                <View style={styles.table}>
                  {ipRows.map((row, rowIdx) => {
                    const isLast = rowIdx === ipRows.length - 1;
                    return (
                      <View key={rowIdx} style={[styles.tableRow, { backgroundColor: rowIdx % 2 === 0 ? '#F8FAFC' : '#FFFFFF', borderBottomWidth: isLast ? 0 : 1, minHeight: 20, paddingVertical: 4 }]} wrap={false}>
                        <Text style={[styles.tableRowCell, { width: '33.3%', fontFamily: 'Courier', fontSize: 8.5 }]}>{row[0] || ''}</Text>
                        <Text style={[styles.tableRowCell, { width: '33.3%', fontFamily: 'Courier', fontSize: 8.5 }]}>{row[1] || ''}</Text>
                        <Text style={[styles.tableRowCell, { width: '33.3%', fontFamily: 'Courier', fontSize: 8.5 }]}>{row[2] || ''}</Text>
                      </View>
                    );
                  })}
                </View>
              </View>
            );
          })}
        </>
      )}

      {showApiAppendix && (
        <>
          <Text style={[styles.sectionTitle, { fontSize: 12, marginTop: 14 }]}>API Endpoints Inventory</Text>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderCell, { width: '15%', textAlign: 'center' }]}>Method</Text>
              <Text style={[styles.tableHeaderCell, { width: '50%' }]}>API Path Route</Text>
              <Text style={[styles.tableHeaderCell, { width: '15%', textAlign: 'center' }]}>Auth Req.</Text>
              <Text style={[styles.tableHeaderCell, { width: '20%' }]}>Notes</Text>
            </View>
            {apiTargets.map(target => (
              (target.endpoints || []).map((ep: any, idx: number) => (
                <View key={`${target.target_id}-${idx}`} style={styles.tableRow}>
                  <Text style={[styles.tableRowCell, { width: '15%', textAlign: 'center', fontWeight: 'bold' }]}>
                    {ep.method || 'GET'}
                  </Text>
                  <Text style={[styles.tableRowCell, { width: '50%', fontFamily: 'Courier' }]}>
                    {ep.value || ep.path || String(ep)}
                  </Text>
                  <Text style={[styles.tableRowCell, { width: '15%', textAlign: 'center' }]}>
                    {ep.auth_required !== false ? 'Yes' : 'No'}
                  </Text>
                  <Text style={[styles.tableRowCell, { width: '20%', color: '#64748B' }]}>
                    {ep.notes || 'In-scope route'}
                  </Text>
                </View>
              ))
            ))}
          </View>
        </>
      )}
      {/* Footer page number */}
      <View style={styles.footer} fixed>
        <Text style={styles.footerLeft}>Capture The Bug - Confidential</Text>
        <Text style={styles.footerRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
      </View>
    </Page>
  );
};

export default AppendixPageV2;
